import { useEffect, useMemo, useState } from "react";
import DealerNav from "./DealerNav";

export default function DealerInvitesPage() {
  const API = process.env.REACT_APP_API_BASE;
  const token = useMemo(() => localStorage.getItem("dealerToken") || "", []);
  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    }),
    [token]
  );
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Create form
  const [emailDomain, setEmailDomain] = useState("");
  const [expiresDays, setExpiresDays] = useState(14);
  const [sendToEmail, setSendToEmail] = useState("");
  const [role, setRole] = useState("DealerSales"); // NEW
  const [creating, setCreating] = useState(false);

  // Resend modal (inline simple)
  const [resendId, setResendId] = useState(null);
  const [resendTo, setResendTo] = useState("");
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState("");

  const fetchInvites = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await fetch(`${API}/dealer/invites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403)
        throw new Error("Not authorized (DealerAdmin only).");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data || []);
    } catch (e) {
      setErr(e.message || "Failed to load invites.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createInvite = async (e) => {
    e.preventDefault();
    setCreating(true);
    setErr("");
    try {
      const body = {
        EmailDomain: emailDomain?.trim() || null,
        ExpiresDays: Math.max(1, Math.min(60, Number(expiresDays) || 14)),
        SendToEmail: sendToEmail?.trim() || null,
        Role: role, // NEW
      };
      const res = await fetch(`${API}/dealer/invites`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(body),
      });
      if (res.status === 401 || res.status === 403)
        throw new Error("Not authorized (DealerAdmin only).");
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }
      setEmailDomain("");
      setExpiresDays(14);
      setSendToEmail("");
      setRole("DealerSales"); // reset
      await fetchInvites();
    } catch (e) {
      setErr(e.message || "Failed to create invite.");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this invite? It will expire immediately.")) return;
    setErr("");
    try {
      const res = await fetch(`${API}/dealer/invites/${id}/revoke`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ Reason: "user_action" }),
      });
      if (res.status === 401 || res.status === 403)
        throw new Error("Not authorized.");
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }
      await fetchInvites();
    } catch (e) {
      setErr(e.message || "Failed to revoke invite.");
    }
  };

  const openResend = (id, suggestedEmail = "") => {
    setResendId(id);
    setResendTo(suggestedEmail);
    setResendMsg("");
  };

  const doResend = async () => {
    if (!resendTo?.trim()) {
      setResendMsg("Enter an email address.");
      return;
    }
    setResending(true);
    setResendMsg("");
    try {
      const res = await fetch(
        `${API}/dealer/invites/${resendId}/resend?to=${encodeURIComponent(
          resendTo.trim()
        )}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.status === 401 || res.status === 403)
        throw new Error("Not authorized.");
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }
      setResendMsg("Invite sent!");
      await fetchInvites();
    } catch (e) {
      setResendMsg(e.message || "Failed to resend.");
    } finally {
      setResending(false);
    }
  };

  const StatusPill = ({ usedAt, expiresAt }) => {
    const now = new Date();
    const exp = expiresAt ? new Date(expiresAt) : null;
    const isExpired = exp && now > exp;
    if (usedAt)
      return (
        <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
          Used
        </span>
      );
    if (isExpired)
      return (
        <span className="px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700">
          Expired
        </span>
      );
    return (
      <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
        Active
      </span>
    );
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
         {/* Dealer navigation always at top */}
           <DealerNav />
      <h1 className="text-2xl font-semibold">Dealer Invites</h1>

      {/* Create form */}
      <form
        onSubmit={createInvite}
        className="mt-4 grid md:grid-cols-5 gap-3 bg-white p-4 rounded-xl border"
      >
        <div>
          <label className="block text-sm text-gray-700 mb-1">
            Restrict to domain (optional)
          </label>
          <input
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            placeholder="dealership.com"
            value={emailDomain}
            onChange={(e) => setEmailDomain(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm text-gray-700 mb-1">Expires (days)</label>
          <input
            type="number"
            min={1}
            max={60}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            value={expiresDays}
            onChange={(e) => setExpiresDays(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm text-gray-700 mb-1">Role</label>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            <option value="DealerSales">DealerSales</option>
            <option value="DealerManager">DealerManager</option>
            <option value="DealerAdmin">DealerAdmin</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm text-gray-700 mb-1">
            Email invite to (optional)
          </label>
          <input
            type="email"
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            placeholder="user@dealership.com"
            value={sendToEmail}
            onChange={(e) => setSendToEmail(e.target.value)}
          />
        </div>
        <div className="md:col-span-5">
          <button
            type="submit"
            disabled={creating}
            className="mt-1 bg-blue-600 text-white rounded-lg px-4 py-2 hover:bg-blue-700 disabled:opacity-50"
          >
            {creating ? "Creating…" : "Create invite"}
          </button>
        </div>
      </form>

      {err && <div className="mt-3 text-sm text-red-600">{err}</div>}

      {/* Invites table */}
      <div className="mt-6 bg-white rounded-xl border overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="text-left px-4 py-2">Join URL</th>
              <th className="text-left px-4 py-2">Role</th>
              <th className="text-left px-4 py-2">Domain</th>
              <th className="text-left px-4 py-2">Expires</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-4 py-4" colSpan={6}>
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="px-4 py-4" colSpan={6}>
                  No invites yet.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <a
                        href={r.joinUrl}
                        className="text-blue-600 underline break-all"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {r.joinUrl}
                      </a>
                      <button
                        type="button"
                        className="text-xs px-2 py-1 rounded border hover:bg-gray-50"
                        onClick={() => navigator.clipboard?.writeText(r.joinUrl)}
                      >
                        Copy
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <span className="px-2 py-0.5 text-xs rounded-full border">
                      {r.role || "DealerSales"}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {r.emailDomain || <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-2">
                    {r.expiresAt
                      ? new Date(r.expiresAt).toLocaleString()
                      : "—"}
                  </td>
                  <td className="px-4 py-2">
                    <StatusPill usedAt={r.usedAt} expiresAt={r.expiresAt} />
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="text-xs px-2 py-1 rounded border hover:bg-gray-50"
                        onClick={() => openResend(r.id)}
                        disabled={
                          !!r.usedAt ||
                          (r.expiresAt && new Date() > new Date(r.expiresAt))
                        }
                      >
                        Resend
                      </button>
                      <button
                        type="button"
                        className="text-xs px-2 py-1 rounded border hover:bg-gray-50 text-red-600 border-red-300"
                        onClick={() => revoke(r.id)}
                        disabled={!!r.usedAt}
                      >
                        Revoke
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Simple resend dialog */}
      {resendId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-4 w-full max-w-sm border">
            <h2 className="text-lg font-semibold">Resend invite</h2>
            <div className="mt-3">
              <label className="block text-sm text-gray-700 mb-1">Send to</label>
              <input
                type="email"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="user@dealership.com"
                value={resendTo}
                onChange={(e) => setResendTo(e.target.value)}
              />
            </div>
            {resendMsg && <div className="text-sm mt-2">{resendMsg}</div>}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-2 rounded border"
                onClick={() => {
                  setResendId(null);
                  setResendTo("");
                  setResendMsg("");
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
                onClick={doResend}
                disabled={resending}
              >
                {resending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
